import { ThunkDispatch } from 'redux-thunk';
import { Thunk } from '../thunk';
import { ReduxState } from '../state';
import { ReduxAction } from '../actions';
import { ResourceApi } from '../../resource/Resource';
import { Course } from '../Courses/types';
import { IDENTITY_SESSION } from '../resourceNames';
import { AZURE_FACE_MATCH_STATUS_NOT_DONE } from './constants';
import {
  currentCourseSelectorJs,
  selectIsAdminOfCurrentCourse,
} from '../Courses/selectors';
import { Portal } from '../Portals/types';
import { TypingDNA } from '../../web/vendor/typingdna/typingdna';
import { selectCurrentCourseLinkedPortalJS } from '../Portals/selectors';
import { selectCurrentUserIsStaffInCurrentCohort } from '../CohortRoles/selectors';
import {
  checkIdentitySession,
  setError,
  setLoading,
  showFaceCheckModal,
} from './dispatchers.common';

export * from './dispatchers.common';

// Check facial identity if it's enabled
export const checkFacialIdentity = (
  currentCourse?: Course,
  currentPortal?: Portal
): Thunk => async (dispatch, getState): Promise<void> => {
  const course = currentCourse || currentCourseSelectorJs(getState());
  const portal = currentPortal || selectCurrentCourseLinkedPortalJS(getState());
  const isCourseAdmin = selectIsAdminOfCurrentCourse(getState());
  const isCohortStaff = selectCurrentUserIsStaffInCurrentCohort(getState());

  if (
    isCourseAdmin ||
    isCohortStaff ||
    !portal ||
    !portal.identitySessionEnabled ||
    !course ||
    !course.facialCheckEnabled
  ) {
    return;
  }

  const identitySession = await checkIdentitySession(course.path);

  if (
    identitySession.azureFaceMatchStatus === AZURE_FACE_MATCH_STATUS_NOT_DONE
  ) {
    dispatch(showFaceCheckModal);
  }
};

export const initializeIdentitySession = (
  dispatch: ThunkDispatch<ReduxState, void, ReduxAction>,
  getState: () => ReduxState
): void => {
  const course = currentCourseSelectorJs(getState());
  const portal = selectCurrentCourseLinkedPortalJS(getState());
  const isCourseAdmin = selectIsAdminOfCurrentCourse(getState());
  const isCohortStaff = selectCurrentUserIsStaffInCurrentCohort(getState());

  if (isCourseAdmin || isCohortStaff) {
    return;
  }

  if (portal && course) {
    if (portal.identitySessionEnabled && course.keystrokeEnabled) {
      // Start recording typing patterns
      window.typingDna = new TypingDNA();
    }
    dispatch(checkFacialIdentity(course, portal));
  }
};

export const processFaceVideo = (blob: Blob): Thunk => async (
  dispatch,
  getState
): Promise<void> => {
  dispatch(setLoading(true));

  const course = currentCourseSelectorJs(getState());
  if (!course) {
    dispatch(setError('No course, please refresh page'));
    return;
  }

  try {
    await ResourceApi.create(
      IDENTITY_SESSION,
      {
        coursePath: course.path,
      },
      {},
      [],
      {
        faceVideo: blob,
      }
    );
  } catch {
    dispatch(setError('Unexpected error, please try again'));
  } finally {
    dispatch(setLoading(false));
  }
};
